import React, { useMemo, useState, useEffect } from 'react'
import { useCycleSessions } from '../../../../hooks/useCycleSessions'
import { IModuleSessionDistribution } from '../../../../types/cycleSession'
import Numbers from './Numbers'
import SessionsChart from './SessionsChart'
import DisciplinesDistribution from './DisciplinesDistribution'
import Activities from './Activities'
import CurrentSession from './CurrentSession'
import StructuredContest from './StructuredContest'
import './styles.css'
import { IStudyPlan } from 'store/modules/studyPlan/types'
import { createColorMap } from 'pages/Planning/utils/chartColors'
import { useGetCourseBasicInfo } from 'pages/Course/hooks/use-get-course-basic-info'
import { useSessionContent } from 'pages/Planning/hooks/useSessionContent'
import { ICycleSession } from 'pages/Planning/types/cycleSession'

interface CycleDashboardProps {
  plan: IStudyPlan
  isDarkMode?: boolean
}

/**
 * Dashboard principal para visualização de ciclos de estudo
 */
const CycleDashboard: React.FC<CycleDashboardProps> = ({ plan, isDarkMode = false }) => {
  const { data, loading, error, refetch } = useCycleSessions(plan?._id)
  const swrCourseBasicInfo = useGetCourseBasicInfo(plan.course?._id || '')

  const [activeTab, setActiveTab] = useState<'current' | 'activities' | 'structured'>('current')

  const sessions = useMemo(() => {
    if (!data?.sessions || !data?.modules) return []

    // Get unique subjects to assign consistent colors
    const uniqueSubjects: string[] = Array.from(new Set(data?.modules.map((m) => String(m._id))))

    // Create color map using shared chart colors utility
    const colorMap = createColorMap(uniqueSubjects)

    const currentSession = data?.sessions.find((s) => s.status !== 'done')

    return data?.sessions.map((session) => ({
      ...session,
      current: currentSession?._id === session._id,
      color: colorMap.get(session.module._id) || '#027A00',
    }))
  }, [data])

  const current = useMemo(() => {
    if (!sessions) return null
    const session = sessions.find((s) => s.current)
    return session
  }, [sessions])

  const {
    data: sessionContent,
    error: sessionContentError,
    loading: sessionContentLoading,
  } = useSessionContent(plan.course._id, current?.module?._id)

  const content = useMemo(() => {
    if (!sessionContent) return []
    return sessionContent
  }, [sessionContent])

  // Calcula total de horas semanais
  const totalWeeklyHours = useMemo(() => {
    return sessions.reduce((sum, item) => sum + item.hours, 0)
  }, [sessions])

  // Escuta evento de sessão finalizada para atualizar o ciclo
  useEffect(() => {
    const handleCycleSessionCompleted = (event: CustomEvent) => {
      // Verifica se o evento é para o plano atual
      if (event.detail?.planId === plan?._id) {
        refetch()
      }
    }

    window.addEventListener('cycleSessionCompleted', handleCycleSessionCompleted as EventListener)

    return () => {
      window.removeEventListener(
        'cycleSessionCompleted',
        handleCycleSessionCompleted as EventListener
      )
    }
  }, [plan?._id, refetch])

  if (loading) {
    return (
      <div className={`cycle-dashboard ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="loading-block" style={{ width: '100%', height: 100 }} />
        <div className="loading-block" style={{ width: '100%', height: 300, marginTop: 20 }} />
        <div className="loading-block" style={{ width: '100%', height: 400, marginTop: 20 }} />
      </div>
    )
  }

  if (error) {
    return (
      <div className={`cycle-dashboard ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="cycle-error">
          <h3>Erro ao carregar dados do ciclo</h3>
          <p>{error}</p>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className={`cycle-dashboard ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="cycle-empty">
          <p>Nenhum dado disponível para este ciclo</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`cycle-dashboard ${isDarkMode ? 'dark' : 'light'}`}>
      {/* Header com informações do ciclo */}
      <div className="cycle-header">
        <div className="cycle-section">
          <div className="cycle-header-info">
            <div className="cycle-header-icon">
              {plan.publicTender?.icon ? (
                <img src={plan.publicTender?.icon} alt="Icon" />
              ) : (
                <div className="contest-option-fallback-icon">
                  {plan.publicTender?.name
                    .trim()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/-/g, '')
                    .split(' ')
                    .filter((p) => p.length > 2)
                    .map((p) => p[0].toUpperCase())
                    .join('')}
                </div>
              )}
            </div>
            <div className="cycle-header-details">
              <h5 className="cycle-header-name">{plan.publicTender?.name}</h5>
              {plan.publicTender?.role && (
                <p className="cycle-header-label">{plan.publicTender?.role}</p>
              )}
            </div>
          </div>
        </div>

        <Numbers
          plan={plan}
          percentage={data.progress.percentage}
          hours={data.progress.hours}
          completedSessions={data.sessions.filter((s) => s.status === 'done').length}
          completedCycles={data.progress.completed}
          totalSessions={data.sessions.length}
        />
      </div>

      {/* Componente de abas */}
      <div className="cycle-content-grid">
        <div className="cycle-tabs-container span-4">
          <div className="cycle-tabs-header">
            <button
              className={`cycle-tab ${activeTab === 'current' ? 'active' : ''}`}
              onClick={() => setActiveTab('current')}
              data-tour="dashboard-tab-current"
            >
              Sessão atual
            </button>
            <button
              className={`cycle-tab ${activeTab === 'activities' ? 'active' : ''}`}
              onClick={() => setActiveTab('activities')}
              data-tour="dashboard-tab-activities"
            >
              Sequência de estudo
            </button>
            <button
              className={`cycle-tab ${activeTab === 'structured' ? 'active' : ''}`}
              onClick={() => setActiveTab('structured')}
              data-tour="dashboard-tab-structured"
            >
              Edital verticalizado
            </button>
          </div>
          <div className="cycle-tabs-content">
            {activeTab === 'current' && (
              <CurrentSession
                current={current}
                content={content}
                plan={plan}
                course={swrCourseBasicInfo.data}
                isLoading={sessionContentLoading}
              />
            )}
            {activeTab === 'activities' && <Activities sessions={sessions} plan={plan} />}
            {activeTab === 'structured' && (
              <StructuredContest
                plan={plan}
                course={swrCourseBasicInfo.data}
                isDarkMode={isDarkMode}
                isLoading={swrCourseBasicInfo.isLoading}
              />
            )}
          </div>
        </div>
        <div className="cycle-right-column span-2">
          <SessionsChart
            sessions={sessions}
            totalHours={totalWeeklyHours}
            isDarkMode={isDarkMode}
          />
          <DisciplinesDistribution
            modules={data.modules}
            sessions={sessions}
            isDarkMode={isDarkMode}
          />
        </div>
      </div>
    </div>
  )
}

export default CycleDashboard
